import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Rating, createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL; // Make sure this points to your backend API

// Create a dark theme using Material-UI's theme creator
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#1E90FF", // Light blue primary color
    },
    background: {
      default: "#000000", // Dark background color
      paper: "#000000", // Slightly lighter background for paper elements
    },
    text: {
      primary: "#ffffff", // Light text color
      secondary: "#b0b0b0", // Lighter text color for secondary text
    },
    error: {
      main: "#f44336", // Red for error messages
    },
  },
});

const FeedbackFormMyRequests = ({ sessionId, requestId, userId }) => {
  const [feedback, setFeedback] = useState({
    rating: 0,
    comments: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);



  useEffect(() => {
    // Check initial validation status when component loads
    checkValidationStatus();
  }, []); // Empty dependency array to run only once

  const checkValidationStatus = async () => {
    try {
      // Send the post_id and user_id in the request body
      const response = await axios.post(`${API_URL}/api/booking-status-request/`, {
        post_id: sessionId,
        user_id: userId,
      });

      // If the response contains validation status
      if (response.data.validation === 'success') {
        setValidationStatus('success');

      }
    } catch (error) {
      console.log('Error fetching validation status', error);

    }
  };

  useEffect(() => {
    const checkExistingFeedback = async () => {
      try {
        const response = await axios.post(`${API_URL}/check_feedback_my_requests/`, {
          session_id: sessionId,
          request_id: requestId,
        });

        if (response.data.detail === "Feedback has already been submitted for this session.") {
          setAlreadySubmitted(true);
        }
      } catch (err) {
        console.error("Error checking existing feedback:", err);
      }
    };

    checkExistingFeedback();
  }, [sessionId, requestId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({
      ...feedback,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (feedback.rating === 0) {
      setError("Please rate the session.");
      setLoading(false);
      return;
    }

    if (feedback.comments.trim() === "") {
      setError("Please provide a comment.");
      setLoading(false);
      return;
    }

    const data = {
      session_id: sessionId,
      request_id: requestId,
      rating: feedback.rating,
      comment: feedback.comments,
    };

    try {
      const response = await axios.post(`${API_URL}/submit_feedback_my_requests/`, data);
      if (response.status === 200) {
        setSubmitted(true);
      } else {
        setError("Something went wrong, please try again later.");
      }
    } catch (err) {
      setError("Failed to submit feedback. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ maxWidth: 500, margin: "0 auto", padding: "20px", marginTop: '100px', borderRadius: "8px", boxShadow: 3, backgroundColor: "background.paper" }}>
        <Typography variant="h4" component="h1" gutterBottom color="text.primary" sx={{ textAlign: 'center', letterSpacing: 4 }}>Session Feedback</Typography>

        {alreadySubmitted ? (
          <Typography variant="h6" color="primary" sx={{ textAlign: 'center', letterSpacing: 3 }}>You have already submitted feedback for this session.</Typography>
        ) : submitted ? (
          <Typography variant="h6" color="primary" sx={{ textAlign: 'center', letterSpacing: 3 }}>Thank you for your feedback!</Typography>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ marginBottom: "0.5rem", textAlign: 'center', letterSpacing: 3 }} color="text.primary">
              How would you rate the session?
            </Typography>
            <Rating
              name="rating"
              value={feedback.rating}
              onChange={(_, newValue) => handleChange({ target: { name: "rating", value: newValue } })}
              precision={0.5}
              sx={{ marginBottom: "1rem", letterSpacing: 3 }}
            />

            <TextField
              label="Comments"
              name="comments"
              value={feedback.comments}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 100 }}
              helperText={`${feedback.comments.length}/100 characters`}
              sx={{ marginBottom: "1rem", letterSpacing: 3 }}
              InputLabelProps={{ style: { color: "white", letterSpacing: 3 } }}
              InputProps={{ style: { color: "white", letterSpacing: 3 } }}
            />


            {error && (
              <Typography variant="body2" color="error" sx={{ marginBottom: "1rem", textAlign: 'center', letterSpacing: 3 }}>
                {error}
              </Typography>
            )}

            <Button variant="contained" color="primary" type="submit" fullWidth
              // disabled={loading} 
              disabled={loading || validationStatus !== 'success'}
              sx={{ letterSpacing: 4 }}>
              {loading ? "Submitting..." : "Submit Feedback"}
            </Button>
          </form>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default FeedbackFormMyRequests;
